import { useId, useState } from 'react';

import { Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { STOCK_MASTER_VALUES, STOCK_TARGET_FIELDS } from '@repo/shared-types';

import type { FieldMapping, MappingTransform } from '@repo/shared-types';

const CUSTOM = '__custom__';
const TRANSFORMS: MappingTransform[] = [
  'text',
  'number',
  'integer',
  'boolean',
  'date',
  'first',
  'list',
  'image_list',
];

export const selectClass =
  'border-input bg-background h-9 rounded-md border px-2 text-sm focus-visible:outline-none focus-visible:ring-1';

/** "Dealer Name" → "dealer_name" (the custom.<name> key format the backend accepts). */
export const toCustomKey = (label: string): string =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[^a-z]+/, '')
    .slice(0, 50);

/** The fixed values for a target (fuel, body, gearbox, drive), or null for free-text fields. */
export const masterValuesFor = (target: string): readonly string[] | null =>
  (STOCK_MASTER_VALUES as Record<string, readonly string[]>)[target] ?? null;

/** Rows of "source value → our value"; '' (leave empty) stores no value. */
const ValueMapEditor = ({
  masters,
  valueMap,
  onChange,
}: {
  masters: readonly string[];
  valueMap: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) => {
  const entries = Object.entries(valueMap);
  const setEntry = (index: number, from: string, to: string) =>
    onChange(Object.fromEntries(entries.map((e, i) => (i === index ? [from, to] : e))));
  return (
    <div className="bg-muted/40 ml-4 space-y-2 rounded-md border p-3">
      <p className="text-muted-foreground text-xs">
        Values that already match ({masters.join(', ')}) need no entry; case is ignored.
      </p>
      {entries.map(([from, to], i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            aria-label="Source value"
            placeholder="value from the API, e.g. 4x4"
            value={from}
            onChange={(e) => setEntry(i, e.target.value, to)}
          />
          <span className="text-muted-foreground">→</span>
          <select
            aria-label={`Translate ${from} to`}
            className={`${selectClass} min-w-40`}
            value={to}
            onChange={(e) => setEntry(i, from, e.target.value)}
          >
            <option value="">(leave empty)</option>
            {masters.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Remove translation"
            onClick={() => onChange(Object.fromEntries(entries.filter((_, j) => j !== i)))}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange({ ...valueMap, '': '' })}
      >
        <Plus className="mr-1 h-4 w-4" />
        Add translation
      </Button>
    </div>
  );
};

interface Props {
  value: FieldMapping[];
  onChange: (next: FieldMapping[]) => void;
  /** Paths from the last Test response, offered as suggestions. */
  paths: string[];
}

export const MappingEditor = ({ value, onChange, paths }: Props) => {
  const listId = useId();
  const update = (i: number, patch: Partial<FieldMapping>) =>
    onChange(value.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  const hasExternalId = value.some((m) => m.target === 'external_id');
  const [open, setOpen] = useState<Set<number>>(new Set());
  const toggleOpen = (i: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <div className="space-y-2">
      <datalist id={listId}>
        {paths.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>
      <div className="text-muted-foreground grid grid-cols-[1.2fr_1.5fr_0.8fr_auto] gap-2 text-xs">
        <span>Save as</span>
        <span>From response path</span>
        <span>Type</span>
        <span />
      </div>
      {value.map((m, i) => {
        const isCustom = m.target.startsWith('custom.');
        const masters = masterValuesFor(m.target);
        const translations = Object.keys(m.value_map ?? {}).length;
        return (
          <div key={i} className="space-y-2">
            <div className="grid grid-cols-[1.2fr_1.5fr_0.8fr_auto] items-center gap-2">
              <div className="flex gap-1">
                <select
                  aria-label="Target field"
                  className={`${selectClass} min-w-0 flex-1`}
                  value={isCustom ? CUSTOM : m.target}
                  onChange={(e) =>
                    update(i, { target: e.target.value === CUSTOM ? 'custom.' : e.target.value })
                  }
                >
                  {STOCK_TARGET_FIELDS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                  <option value={CUSTOM}>Custom field…</option>
                </select>
                {isCustom && (
                  <Input
                    aria-label="Custom field name"
                    placeholder="dealer_name"
                    className="min-w-0 flex-1"
                    value={m.target.slice('custom.'.length)}
                    onChange={(e) => update(i, { target: `custom.${toCustomKey(e.target.value)}` })}
                  />
                )}
              </div>
              <Input
                aria-label="Source path"
                placeholder="build.make"
                list={listId}
                value={m.path}
                onChange={(e) => update(i, { path: e.target.value })}
              />
              <select
                aria-label="Transform"
                className={selectClass}
                value={m.transform}
                onChange={(e) => update(i, { transform: e.target.value as MappingTransform })}
              >
                {TRANSFORMS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <div className="flex items-center">
                {masters && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleOpen(i)}
                    title="Translate source values to our fixed values"
                  >
                    Values ({translations})
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label="Remove field"
                  onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {masters && open.has(i) && (
              <ValueMapEditor
                masters={masters}
                valueMap={m.value_map ?? {}}
                onChange={(value_map) => update(i, { value_map })}
              />
            )}
          </div>
        );
      })}
      {!hasExternalId && (
        <p className="text-destructive text-sm">
          Map one field to external_id — it identifies each car between syncs.
        </p>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() =>
          onChange([...value, { target: 'vehicle.model', path: '', transform: 'text' }])
        }
      >
        <Plus className="mr-1 h-4 w-4" />
        Add field
      </Button>
    </div>
  );
};
